import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus } from 'lucide-react';
import api from '../api';

// Shadcn UI Components
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';

export default function LessorCreate({ mode = 'create', defaults = {}, id }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("basic");

  /* RHF */
  const { control, handleSubmit, reset } = useForm({ defaultValues: defaults });
  const reps = useFieldArray({ control, name: 'representantes' });
  const patios = useFieldArray({ control, name: 'patios' });

  /* helpers de checkbox */

  const CheckArray = ({ name, value, label }) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const values = field.value || [];
        const isChecked = values.includes(value);
        
        return (
          <div className="flex items-center space-x-2">
            <Checkbox 
              id={`${name}-${value}`} 
              checked={isChecked}
              onCheckedChange={(checked) => {
                if (checked) {
                  field.onChange([...values, value]);
                } else {
                  field.onChange(values.filter(v => v !== value));
                }
              }}
            />
            <Label htmlFor={`${name}-${value}`} className="text-sm font-medium">
              {label}
            </Label>
          </div>
        );
      }}
    />
  );

  /* submit */
  const onSubmit = data => {
    const req = mode === 'create'
      ? api.post('/locadoras', data)
      : api.put(`/locadoras/${id}`, data);
    
    req.then(() => {
      reset();
      navigate('/admin/locadoras');
    }).catch(err => {
      alert(err.response?.data?.error || err.message);
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">
          {mode === 'create' ? t('lessor.create.title') : t('lessor.edit.title')}
        </h1>
        <p className="text-muted-foreground mt-2">
          {mode === 'create' 
            ? 'Cadastre uma nova locadora com suas informações completas' 
            : 'Edite as informações da locadora cadastrada'}
        </p>
      </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab} 
            className="w-full"
          >
            <TabsList className="mb-6">
              <TabsTrigger value="basic">{t('lessor.tabs.basic')}</TabsTrigger>
              <TabsTrigger value="representatives">{t('lessor.tabs.representatives')}</TabsTrigger>
              <TabsTrigger value="locations">{t('lessor.tabs.locations')}</TabsTrigger>
              <TabsTrigger value="operational">{t('lessor.tabs.operational')}</TabsTrigger>
            </TabsList>

            {/* Tab: Informações Básicas */}
            <TabsContent value="basic">
              <Card className="p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-2">Dados da Empresa</h3>
                  <p className="text-muted-foreground text-sm">Informações básicas e cadastrais</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Controller
                      name="nomeFantasia"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="nomeFantasia" className="text-sm font-semibold">
                            {t('lessor.fields.nomeFantasia')} <span className="text-destructive">*</span>
                          </Label>
                          <Input 
                            {...field} 
                            id="nomeFantasia" 
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div>
                    <Controller
                      name="razaoSocial"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="razaoSocial" className="text-sm font-semibold">
                            {t('lessor.fields.razaoSocial')} <span className="text-destructive">*</span>
                          </Label>
                          <Input 
                            {...field} 
                            id="razaoSocial" 
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div>
                    <Controller
                      name="cnpj"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="cnpj" className="text-sm font-semibold">
                            {t('lessor.fields.cnpj')} <span className="text-destructive">*</span>
                          </Label>
                          <Input 
                            {...field} 
                            id="cnpj" 
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div>
                    <Controller
                      name="inscricaoEstadual"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="inscricaoEstadual" className="text-sm font-semibold">
                            {t('lessor.fields.inscricaoEstadual')}
                          </Label>
                          <Input 
                            {...field} 
                            id="inscricaoEstadual" 
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Controller
                      name="grupoEmpresarial"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="grupoEmpresarial" className="text-sm font-semibold">
                            {t('lessor.fields.grupoEmpresarial')}
                          </Label>
                          <Input 
                            {...field} 
                            id="grupoEmpresarial" 
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>
              </Card>

              <Card className="p-6 mt-8">
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-2">{t('lessor.sections.address')}</h3>
                  <p className="text-muted-foreground text-sm">Endereço da sede principal</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                  <div className="md:col-span-3">
                    <Controller
                      name="endereco.cep"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="endereco-cep" className="text-sm font-semibold">
                            {t('lessor.address.cep')}
                          </Label>
                          <Input 
                            {...field} 
                            id="endereco-cep"
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div className="md:col-span-7">
                    <Controller
                      name="endereco.logradouro"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="endereco-logradouro" className="text-sm font-semibold">
                            {t('lessor.address.logradouro')}
                          </Label>
                          <Input 
                            {...field} 
                            id="endereco-logradouro"
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Controller
                      name="endereco.numero"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="endereco-numero" className="text-sm font-semibold">
                            {t('lessor.address.numero')}
                          </Label>
                          <Input 
                            {...field} 
                            id="endereco-numero"
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div className="md:col-span-4">
                    <Controller
                      name="endereco.complemento"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="endereco-complemento" className="text-sm font-semibold">
                            {t('lessor.address.complemento')}
                          </Label>
                          <Input 
                            {...field} 
                            id="endereco-complemento"
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div className="md:col-span-4">
                    <Controller
                      name="endereco.bairro"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="endereco-bairro" className="text-sm font-semibold">
                            {t('lessor.address.bairro')}
                          </Label>
                          <Input 
                            {...field} 
                            id="endereco-bairro"
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div className="md:col-span-3">
                    <Controller
                      name="endereco.cidade"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="endereco-cidade" className="text-sm font-semibold">
                            {t('lessor.address.cidade')}
                          </Label>
                          <Input 
                            {...field} 
                            id="endereco-cidade"
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div className="md:col-span-1">
                    <Controller
                      name="endereco.estado"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="endereco-estado" className="text-sm font-semibold">
                            {t('lessor.address.estado')}
                          </Label>
                          <Input 
                            {...field} 
                            id="endereco-estado"
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>
              </Card>

              <Card className="p-6 mt-8">
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-2">{t('lessor.sections.mainContact')}</h3>
                  <p className="text-muted-foreground text-sm">Informações do contato principal</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Controller
                      name="contatoPrincipal.nome"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="contato-nome" className="text-sm font-semibold">
                            {t('lessor.contact.nome')}
                          </Label>
                          <Input 
                            {...field} 
                            id="contato-nome"
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div>
                    <Controller
                      name="contatoPrincipal.email"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="contato-email" className="text-sm font-semibold">
                            {t('lessor.contact.email')}
                          </Label>
                          <Input 
                            {...field} 
                            id="contato-email"
                            type="email"
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div>
                    <Controller
                      name="contatoPrincipal.telefone"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="contato-telefone" className="text-sm font-semibold">
                            {t('lessor.contact.telefone')}
                          </Label>
                          <Input 
                            {...field} 
                            id="contato-telefone"
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>

                  <div>
                    <Controller
                      name="contatoPrincipal.celular"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <Label htmlFor="contato-celular" className="text-sm font-semibold">
                            {t('lessor.contact.celular')}
                          </Label>
                          <Input 
                            {...field} 
                            id="contato-celular"
                            className="w-full h-11" 
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Tab: Representantes */}
            <TabsContent value="representatives">
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-2">{t('lessor.sections.representatives')}</h3>
                <p className="text-muted-foreground text-sm">Adicione os representantes da locadora</p>
              </div>
              
              {reps.fields.length === 0 && (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground mb-4">Nenhum representante cadastrado</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => reps.append({ nome: '', departamento: '', celular: '', email: '' })}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('lessor.actions.addRepresentative')}
                  </Button>
                </Card>
              )}

              {reps.fields.length > 0 && (
                <div className="space-y-6">
                  {reps.fields.map((rep, idx) => (
                    <Card 
                      key={rep.id} 
                      className="p-6"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold">Representante #{idx + 1}</h4>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => reps.remove(idx)}
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {[
                          ['nome', t('lessor.representative.nome')],
                          ['departamento', t('lessor.representative.departamento')],
                          ['celular', t('lessor.representative.celular')],
                          ['email', t('lessor.representative.email')]
                        ].map(([key, label]) => (
                          <div key={key}>
                            <Controller
                              name={`representantes.${idx}.${key}`}
                              control={control}
                              render={({ field }) => (
                                <div className="space-y-2">
                                  <Label htmlFor={`rep-${idx}-${key}`} className="text-sm font-semibold">
                                    {label}
                                  </Label>
                                  <Input 
                                    {...field} 
                                    id={`rep-${idx}-${key}`}
                                    className="w-full h-11" 
                                  />
                                </div>
                              )}
                            />
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => reps.append({ nome: '', departamento: '', celular: '', email: '' })}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('lessor.actions.addRepresentative')}
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Tab: Pátios */}
            <TabsContent value="locations">
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-2">{t('lessor.sections.locations')}</h3>
                <p className="text-muted-foreground text-sm">Cadastre os pátios da locadora</p>
              </div>

              {patios.fields.length === 0 && (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground mb-4">Nenhum pátio cadastrado</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => patios.append({ 
                      cep: '', 
                      logradouro: '', 
                      numero: '', 
                      complemento: '', 
                      bairro: '', 
                      cidade: '', 
                      estado: '' 
                    })}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('lessor.actions.addLocation')}
                  </Button>
                </Card>
              )}
              
              {patios.fields.length > 0 && (
                <div className="space-y-6">
                  {patios.fields.map((patio, idx) => (
                    <Card 
                      key={patio.id} 
                      className="p-6"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold">Pátio #{idx + 1}</h4>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => patios.remove(idx)}
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                        <div className="md:col-span-3">
                          <Controller
                            name={`patios.${idx}.cep`}
                            control={control}
                            render={({ field }) => (
                              <div className="space-y-2">
                                <Label htmlFor={`patio-${idx}-cep`} className="text-sm font-semibold">
                                  {t('lessor.address.cep')}
                                </Label>
                                <Input 
                                  {...field} 
                                  id={`patio-${idx}-cep`}
                                  className="w-full h-11" 
                                />
                              </div>
                            )}
                          />
                        </div>
                        <div className="md:col-span-7">
                          <Controller
                            name={`patios.${idx}.logradouro`}
                            control={control}
                            render={({ field }) => (
                              <div className="space-y-2">
                                <Label htmlFor={`patio-${idx}-logradouro`} className="text-sm font-semibold">
                                  {t('lessor.address.logradouro')}
                                </Label>
                                <Input 
                                  {...field} 
                                  id={`patio-${idx}-logradouro`}
                                  className="w-full h-11" 
                                />
                              </div>
                            )}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Controller
                            name={`patios.${idx}.numero`}
                            control={control}
                            render={({ field }) => (
                              <div className="space-y-2">
                                <Label htmlFor={`patio-${idx}-numero`} className="text-sm font-semibold">
                                  {t('lessor.address.numero')}
                                </Label>
                                <Input 
                                  {...field} 
                                  id={`patio-${idx}-numero`}
                                  className="w-full h-11" 
                                />
                              </div>
                            )}
                          />
                        </div>
                        <div className="md:col-span-6">
                          <Controller
                            name={`patios.${idx}.complemento`}
                            control={control}
                            render={({ field }) => (
                              <div className="space-y-2">
                                <Label htmlFor={`patio-${idx}-complemento`} className="text-sm font-semibold">
                                  {t('lessor.address.complemento')}
                                </Label>
                                <Input 
                                  {...field} 
                                  id={`patio-${idx}-complemento`}
                                  className="w-full h-11" 
                                />
                              </div>
                            )}
                          />
                        </div>
                        <div className="md:col-span-6">
                          <Controller
                            name={`patios.${idx}.bairro`}
                            control={control}
                            render={({ field }) => (
                              <div className="space-y-2">
                                <Label htmlFor={`patio-${idx}-bairro`} className="text-sm font-semibold">
                                  {t('lessor.address.bairro')}
                                </Label>
                                <Input 
                                  {...field} 
                                  id={`patio-${idx}-bairro`}
                                  className="w-full h-11" 
                                />
                              </div>
                            )}
                          />
                        </div>
                        <div className="md:col-span-8">
                          <Controller
                            name={`patios.${idx}.cidade`}
                            control={control}
                            render={({ field }) => (
                              <div className="space-y-2">
                                <Label htmlFor={`patio-${idx}-cidade`} className="text-sm font-semibold">
                                  {t('lessor.address.cidade')}
                                </Label>
                                <Input 
                                  {...field} 
                                  id={`patio-${idx}-cidade`}
                                  className="w-full h-11" 
                                />
                              </div>
                            )}
                          />
                        </div>
                        <div className="md:col-span-4">
                          <Controller
                            name={`patios.${idx}.estado`}
                            control={control}
                            render={({ field }) => (
                              <div className="space-y-2">
                                <Label htmlFor={`patio-${idx}-estado`} className="text-sm font-semibold">
                                  {t('lessor.address.estado')}
                                </Label>
                                <Input 
                                  {...field} 
                                  id={`patio-${idx}-estado`}
                                  className="w-full h-11" 
                                />
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </Card>
                  ))}
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => patios.append({ 
                      cep: '', 
                      logradouro: '', 
                      numero: '', 
                      complemento: '', 
                      bairro: '', 
                      cidade: '', 
                      estado: '' 
                    })}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('lessor.actions.addLocation')}
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Tab: Dados Operacionais */}
            <TabsContent value="operational">
              <div className="space-y-8">
                <Card className="p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-2">{t('lessor.sections.segments')}</h3>
                    <p className="text-muted-foreground text-sm">Selecione os segmentos de atuação</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <CheckArray name="segmentos" value="rental" label={t('lessor.segments.rental')} />
                    <CheckArray name="segmentos" value="fleet" label={t('lessor.segments.fleet')} />
                    <CheckArray name="segmentos" value="sales" label={t('lessor.segments.sales')} />
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-2">{t('lessor.sections.regions')}</h3>
                    <p className="text-muted-foreground text-sm">Regiões de atuação da locadora</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <CheckArray name="regioes" value="sul" label={t('lessor.regions.sul')} />
                    <CheckArray name="regioes" value="sudeste" label={t('lessor.regions.sudeste')} />
                    <CheckArray name="regioes" value="centrooeste" label={t('lessor.regions.centrooeste')} />
                    <CheckArray name="regioes" value="nordeste" label={t('lessor.regions.nordeste')} />
                    <CheckArray name="regioes" value="norte" label={t('lessor.regions.norte')} />
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-2">{t('lessor.sections.observations')}</h3>
                    <p className="text-muted-foreground text-sm">Observações gerais sobre a locadora</p>
                  </div>
                  <Controller
                    name="observacoes"
                    control={control}
                    render={({ field }) => (
                      <Textarea 
                        {...field}
                        rows={4}
                        className="w-full" 
                        placeholder={t('lessor.placeholders.observations')}
                      />
                    )}
                  />
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          {/* Footer */}
          <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
            >
              {mode === 'create' ? t('common.save') : t('common.update')}
            </Button>
          </div>
        </form>
    </div>
  );
}
